import React, { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { ArrowDown, ChevronLeft, ChevronRight } from "lucide-react";
import { TID } from "../../lib/testIds";

const SLIDES = [
  {
    src: "https://customer-assets-7cd3h4nn.emergentagent.net/job_akr-renders/artifacts/bpkfdu4s_IDp9YNTBWcFFd8LKvZ5WEL7ns4AEI5_HdafLf9td8A0fDsBAZzncQvl_D9ea9EGKW39LrYjdFW5m-I8xtYjlhcPh1gH6ODYtwB3knnQ-hUKQU8qMr1TKXH8wzIThPbhJ4tMtqhefdOehPf8OhDT1CTv_5gAiNFCvCtH8mGcciJeJlUdnaHwPB7FcKO1sTRzS.webp",
    alt: "Red steel moment frames rising against a clear sky",
    tag: "Moment frames · Erection",
    caption: "Red iron, rising.",
    drift: -14,
  },
  {
    src: "https://customer-assets-7cd3h4nn.emergentagent.net/job_akr-renders/artifacts/tmnmcprz_N6UsUWl3kLbhvDmJo_kxisx_u-bv66vBqyRW2tHx4s7CcQJQTMPAvDUQBBzOpABmMoMDwsZJqSDdLaq0XrfgeO4ZmQ3zYTHmnkKUlFMgevrVN5anSfCVVk9I6-4KsVqu4s90pE5tiAr2LPqzrr48usjHJ6lE6n_f1S6eGoL4Pl0Uj7-Bzx4GRHLrGarwxpUu.webp",
    alt: "Wide structural steel frame with crawler crane on site",
    tag: "Long-span · Structural frames",
    caption: "Steel set to sequence.",
    drift: 12,
  },
  {
    src: "https://customer-assets-7cd3h4nn.emergentagent.net/job_akr-renders/artifacts/d6f2talx_xA2PFVRXyBQNiBHsj_ABhNOVkN8Zn8gbb1uZY_sXnUbM3Z516vNaAMbxw9iG1JaHDZ0p6ADo-j5TkabK9wY9u97yHlc8wVR69VjI8T05fjQZ9PYp-zOeyZtsaQwQ6DWqhXdRnA4kX6v5YUm8i31D907ozJEF_MXh4AT48Ed0cZk.jpg",
    alt: "American flag flown from a crane hook over a steel structure",
    tag: "Topping out · US jobsites",
    caption: "Topping out, American steel.",
    drift: -10,
  },
];

const SLIDE_MS = 6500;

export const Hero = ({ onExplore, onContact }) => {
  const [index, setIndex] = useState(0);
  const slide = SLIDES[index];

  // Preload slides
  useEffect(() => {
    SLIDES.forEach((s) => {
      const img = new Image();
      img.src = s.src;
    });
  }, []);

  // Auto-advance (timer resets on manual navigation)
  useEffect(() => {
    const t = setTimeout(
      () => setIndex((i) => (i + 1) % SLIDES.length),
      SLIDE_MS
    );
    return () => clearTimeout(t);
  }, [index]);

  const goTo = (i) =>
    setIndex(((i % SLIDES.length) + SLIDES.length) % SLIDES.length);

  return (
    <section
      id="top"
      data-testid={TID.hero.root}
      className="relative min-h-screen overflow-hidden bg-[#0A2540] flex items-center"
    >
      {/* Cinematic slider */}
      <div className="absolute inset-0">
        <AnimatePresence>
          <motion.div
            key={index}
            className="absolute inset-0"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 1.4, ease: "easeInOut" }}
            data-testid={TID.hero.slide(index)}
          >
            <motion.img
              src={slide.src}
              alt={slide.alt}
              className="w-full h-full object-cover"
              initial={{ scale: 1.02, x: 0 }}
              animate={{ scale: 1.1, x: slide.drift }}
              transition={{ duration: SLIDE_MS / 1000 + 1.6, ease: "linear" }}
            />
          </motion.div>
        </AnimatePresence>
      </div>

      {/* Readability overlays */}
      <div className="absolute inset-0 bg-gradient-to-r from-[#081C33]/85 via-[#0A2540]/50 to-[#0A2540]/20 pointer-events-none" />
      <div className="absolute inset-0 bg-gradient-to-t from-[#081C33]/80 via-transparent to-[#081C33]/35 pointer-events-none" />

      {/* Content */}
      <div className="relative z-10 w-full px-6 md:px-10 lg:px-14 xl:px-16 pt-28 pb-40 [@media(max-height:860px)]:pt-24 [@media(max-height:860px)]:pb-28">
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.15 }}
          className="inline-flex items-center gap-2.5 border border-white/25 bg-white/10 backdrop-blur-md rounded-full px-4 py-1.5 mb-8"
        >
          <span className="w-1.5 h-1.5 rounded-full bg-[#FCA5A5]" />
          <span className="text-[11px] font-mono uppercase tracking-[0.2em] text-white/85">
            Structural Steel Detailing · Tekla · SDS2
          </span>
        </motion.div>

        <motion.h1
          initial={{ opacity: 0, y: 18 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.3 }}
          className="font-display font-semibold tracking-tight leading-[1.06] text-4xl sm:text-5xl lg:text-6xl xl:text-7xl text-[#FCA5A5] max-w-4xl"
        >
          Structures we build
          <br />
          from steel.
        </motion.h1>

        <motion.p
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.45 }}
          className="mt-6 max-w-xl text-base md:text-lg text-slate-200/90 leading-relaxed"
        >
          Fabrication-ready shop drawings, erection plans and BIM coordination —
          detailed by{" "}
          <span className="text-white font-semibold">
            AKR Steel Detailing Services LLC
          </span>
          , from steel truss bridges to 1,000-ft skyscrapers.
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.58 }}
          className="mt-8 flex flex-wrap items-center gap-4"
        >
          <button
            data-testid={TID.hero.cta}
            onClick={onExplore}
            className="inline-flex items-center gap-2 bg-white hover:bg-slate-100 text-[#0A2540] font-semibold px-6 py-3.5 rounded-full transition-colors"
          >
            Explore the gallery
            <ArrowDown className="w-4 h-4" strokeWidth={2.5} />
          </button>
          <button
            data-testid={TID.hero.ctaSecondary}
            onClick={onContact}
            className="inline-flex items-center gap-2 bg-transparent hover:bg-white/10 text-white font-semibold px-6 py-3.5 rounded-full border border-white/30 transition-colors"
          >
            Start a project
          </button>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.72 }}
          className="mt-12 grid grid-cols-3 gap-4 max-w-md [@media(max-height:860px)]:hidden"
        >
          {[
            { k: "850+", v: "Projects delivered" },
            { k: "48K", v: "Tons / year" },
            { k: "24hr", v: "Response" },
          ].map((s) => (
            <div key={s.v} className="border-l border-white/30 pl-4">
              <div className="font-display text-2xl md:text-3xl font-semibold text-white">
                {s.k}
              </div>
              <div className="text-[10px] md:text-xs uppercase tracking-widest text-slate-300/80 font-mono mt-1">
                {s.v}
              </div>
            </div>
          ))}
        </motion.div>
      </div>

      {/* Bottom bar: slide caption · controls */}
      <div className="absolute inset-x-0 bottom-0 z-10 px-6 md:px-10 lg:px-14 xl:px-16 pb-7 flex items-end justify-between gap-6">
        <div className="flex-1 hidden sm:block">
          <AnimatePresence mode="wait">
            <motion.div
              key={index}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.4 }}
              data-testid={TID.hero.caption}
            >
              <div className="text-[10px] font-mono uppercase tracking-[0.24em] text-slate-300">
                {slide.tag}
              </div>
              <div className="mt-1 font-display text-xl md:text-2xl font-semibold text-white">
                {slide.caption}
              </div>
            </motion.div>
          </AnimatePresence>
        </div>

        <div className="flex items-center gap-4">
          <div className="flex items-center gap-3">
            {SLIDES.map((s, i) => (
              <button
                key={s.src}
                onClick={() => goTo(i)}
                data-testid={TID.hero.dot(i)}
                aria-label={`Show slide ${i + 1}`}
                className="group flex items-center gap-2"
              >
                <span
                  className={`text-[10px] font-mono tracking-[0.18em] transition-colors ${
                    i === index
                      ? "text-white"
                      : "text-white/40 group-hover:text-white/70"
                  }`}
                >
                  0{i + 1}
                </span>
                <span className="relative w-10 h-px bg-white/25 overflow-hidden">
                  {i === index && (
                    <motion.span
                      key={`progress-${index}`}
                      className="absolute inset-y-0 left-0 bg-white"
                      initial={{ width: "0%" }}
                      animate={{ width: "100%" }}
                      transition={{ duration: SLIDE_MS / 1000, ease: "linear" }}
                    />
                  )}
                </span>
              </button>
            ))}
          </div>
          <div className="hidden sm:flex items-center gap-2">
            <button
              onClick={() => goTo(index - 1)}
              data-testid={TID.hero.prev}
              aria-label="Previous slide"
              className="w-10 h-10 rounded-full border border-white/25 bg-white/5 backdrop-blur text-white flex items-center justify-center hover:bg-white hover:text-[#0A2540] transition-colors"
            >
              <ChevronLeft className="w-4 h-4" strokeWidth={2.25} />
            </button>
            <button
              onClick={() => goTo(index + 1)}
              data-testid={TID.hero.next}
              aria-label="Next slide"
              className="w-10 h-10 rounded-full border border-white/25 bg-white/5 backdrop-blur text-white flex items-center justify-center hover:bg-white hover:text-[#0A2540] transition-colors"
            >
              <ChevronRight className="w-4 h-4" strokeWidth={2.25} />
            </button>
          </div>
        </div>
      </div>
    </section>
  );
};
